import { InputField, InputSelectOptions } from 'components'
import LoaderButton from 'components/molecules/loader-button'
import { AUTO_DISCOUNT_OPTIONS, DELIVERY_METHOD, DROPOFF_LOCATION } from 'domains/seller-inbound/constants/seller-inbound-constants'
import SellerSelectSearchField from 'domains/seller/molecules/select-search-field'
import { Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useState, useEffect, useCallback } from 'react'
import { Alert } from 'reactstrap'
import SellersApiV2 from 'api/v2/admins/sellers-api-v2'
import UsersApiV2 from 'api/v2/admins/users-api-v2'
import { Button } from 'reactstrap'
import { Link } from 'react-router-dom'
import consignmentInboundRequestCreateSchema from './consignment-inbound-request-create-schema'
import ConsignmentInboundRequestAddressSection from '../address-section'
import ConsignmentInboundRequestAddressModal from '../address-modal'
import ConsignmentInboundRequestAddressCard from '../address-card'
import ConsignmentInboundRequestsApi from 'api/v2/admins/consignment-inbound-requests-api'
import StringUtil from 'utils/string-util'
import { ALERT_TYPES } from 'constants/alert-constants'

const propTypes = {
  handleSubmit    : PropTypes.func.isRequired,
  onChangeSeller  : PropTypes.func,
  addresses       : PropTypes.array,
  selectedAddress : PropTypes.object,
  pageUtils       : PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const LUXURY_DROPOFF_LOCATIONS = [
  DROPOFF_LOCATION.PALEMBANG.value,
  DROPOFF_LOCATION.BANDUNG.value,
  DROPOFF_LOCATION.SURABAYA.value
]

const DropoffOptions     = ({ locations }) => (
  <>
    <option key='' value=''>Choose one</option>
    {
      locations.map((location) => (
        <option
          key={ `dropoff-location-option-${location.id}` }
          value={ location.id }
        >
          { StringUtil.capitalizeAll(location.name) }
        </option>
      ))
    }
  </>
)
DropoffOptions.propTypes = { locations: PropTypes.array }

/**
 * Form to create new Seller Inbound
 * @param {func} handleSubmit Handler for submitting the form
 * @param {Object} pageUtils as controlled varibale for alert/error message
 */
const ConsignmentInboudRequestCreateForm = ({ handleSubmit, pageUtils }) => {
  const [ userId, setUserId ]                                 = useState(undefined)
  const [ isinputAble, setIsInputAble ]                       = useState(true)
  const [ dropoffCities, setDropoffCities ]                   = useState([])
  const [ luxuryDropoffLocations, setLuxuryDropoffLocations ] = useState([])

  const isDropoff = (values) => values.shipping_method === DELIVERY_METHOD.DROPOFF
  const isPickup  = (values) => values.shipping_method === DELIVERY_METHOD.PICKUP

  const handleChangeSeller = ({ value }) => {
    SellersApiV2.show(value)
      .then((response) => {
        setUserId(response.data.seller.user_id)
      })
      .catch(() => pageUtils.setAlertMsg('Something wrong when showing Seller', ALERT_TYPES.DANGER))
  }

  const handleSelectAddress = (address, formik) => formik.setFieldValue('pickup_address', address)

  useEffect(() => {
    ConsignmentInboundRequestsApi.dropoffCities()
      .then((response) => {
        const cities          = response.data.cities
        const luxuryLocations = cities.filter((city) => LUXURY_DROPOFF_LOCATIONS.includes(city.name))
        setDropoffCities(cities)
        setLuxuryDropoffLocations(luxuryLocations)
      }).catch(() => pageUtils.setAlertMsg('Something wrong when fetch dropoff location', ALERT_TYPES.DANGER))
  }, [])

  useEffect(() => {
    if (!userId) setIsInputAble(false)
    if (userId) setIsInputAble(true)
  },[ userId ])


  const handleFetchError = (error) => pageUtils.setApiErrorMsg(error.response.data)

  const initialParams = {
    seller                   : '',
    quantity                 : 0,
    shipping_method          : '',
    is_auto_discount         : false,
    scheduled_at             : '',
    pickup_address           : null,
    dropoff_location_city_id : '',
    notes                    : ''
  }

  return (
    <Formik
      enableReinitialize={ false }
      initialValues={ initialParams }
      validationSchema={ consignmentInboundRequestCreateSchema }
      onSubmit={ handleSubmit }
    >
      {
        (formikBag) => (
          <Form>
            <SellerSelectSearchField
              name='seller'
              label='Seller'
              required={ true }
              onChange={ (value) => handleChangeSeller(value) }
            />
            {
              userId === null &&(
                <Alert color='info'>
                  <i className='fas fa-info-circle mr-2' />
                  { 'This seller do not have any User Account. Please Link the Account before create Inbound.' }
                  <Button
                    className='ml-5'
                    color='success'
                    tag={ Link }
                    to={ '/app/customer-service/account-link' }
                  >
                    { 'Account Link' }
                  </Button>
                </Alert>
              )
            }
            <InputField
              type='select'
              label="Shipping Method"
              name='shipping_method'
              disabled= { !isinputAble }
              required={ true }
              formText={
                <>
                  <strong>Dropoff: </strong>Seller mengirimkan barangnya ke warehouse
                  <br />
                  <strong>Pickup: </strong>Tinkerlust akan mengirimkan kurir ke alamat seller untuk mengambil barang
                </>
              }
            >
              <InputSelectOptions options={ DELIVERY_METHOD } keyPrefix={ 'shipping_method' } />
            </InputField>

            <InputField
              type='number'
              name='quantity'
              label='Quantity'
              required={ true }
              disabled= { !isinputAble }
              formText='Jumlah barang Seller yang ingin dijual'
            />

            <InputField
              type='select'
              label="Auto Discount"
              name='is_auto_discount'
              disabled= { !isinputAble }
              required={ true }
              formText={
                <span>
                  { 'Barang akan otomatis di diskon pada rentang waktu tertentu ' }
                  <a target='_blank' href='https://www.tinkerlust.com/faq/selling/consignment/automated-discount' rel="noreferrer">
                    { 'lihat detail' }
                  </a>
                </span>
              }
            >
              <InputSelectOptions options={ AUTO_DISCOUNT_OPTIONS } keyPrefix={ 'is_auto_discount' } />
            </InputField>

            <InputField
              type='date'
              name='scheduled_at'
              label='Scheduled At'
              disabled= { !isinputAble }
              required={ true }
              formText='Tanggal seller akan mengirimkan barang'
            />


            { (isDropoff(formikBag.values)) && (
              <>
                <InputField
                  type='select'
                  name='dropoff_location_city_id'
                  label='Dropoff Location'
                  disabled= { !isinputAble }
                  required={ true }
                >
                  <DropoffOptions locations={ dropoffCities }/>
                </InputField>
                {
                  luxuryDropoffLocations.find((city) => city.id == formikBag.values?.dropoff_location_city_id) && (
                    <Alert
                      color='info'
                      className='small'
                    >
                      <i className='fas fa-info-circle mr-3' />
                      { 'Alamat di ' }<b>{ 'Palembang' }</b>
                      { ', ' }<b>{ 'Bandung' }</b>
                      { ' dan ' }<b>{ 'Surabaya' }</b>
                      { ' khusus untuk barang luxury.' }
                    </Alert>
                  )
                }
              </>
            ) }

            {
              isPickup(formikBag.values) &&
              <PickupForm
                userId= { userId }
                selectedAddress={ formikBag.values?.pickup_address }
                handleSelectAddress={ (address) => handleSelectAddress(address, formikBag) }
                onFetchError={ handleFetchError }
              />
            }
            <InputField
              type='text'
              name='notes'
              label='Notes'
              placeholder={ 'Notes (optional)' }
              disabled= { !isinputAble }
            />
            <LoaderButton
              color='primary'
              type='submit'
              onSubmit={ formikBag.submitForm }
              disabled={ formikBag.isSubmitting  }
              isLoading={ formikBag.isSubmitting }
              text='Create Inbound'
            />

          </Form>
        )
      }
    </Formik>
  )
}

const PickupPropTypes = {
  userId              : PropTypes.number,
  selectedAddress     : PropTypes.object,
  handleSelectAddress : PropTypes.func,
  onFetchError        : PropTypes.func
}

const PickupForm = ({
  selectedAddress,
  handleSelectAddress,
  userId,
  onFetchError
}) => {
  const [ isAddressModalOpen, setIsAddressModalOpen ] = useState(false)
  const [ addresses, setAddresses ]                   = useState([])

  useEffect(() => {
    fetchAddresses(userId)
  },[ userId ])

  const fetchAddresses = useCallback((userId) => {
    if (!userId) return
    UsersApiV2.getAddresses({ userId: userId })
      .then((response) => {
        const userAddresses = response.data.addresses
        let defaultAddress  = userAddresses.find((userAddress) => userAddress.is_seller_default == true)
        defaultAddress      = defaultAddress || userAddresses[0]

        setAddresses(userAddresses)
        if (defaultAddress) handleSelectAddress(defaultAddress)
      })
      .catch((error) => {
        onFetchError(error.response.data)
      })
  }, [])

  return (
    <ConsignmentInboundRequestAddressSection
      header={ 'Address' }
      handleClickEdit={ () => setIsAddressModalOpen(true) }
    >
      {
        selectedAddress
          ? (
            <ConsignmentInboundRequestAddressCard
              address={ selectedAddress }
            />
          )
          :
          <Alert color='info'><i className='fas fa-info-circle mr-2' />This seller do not have any address saved. Please add an address to create and inbound.</Alert>
      }

      <ConsignmentInboundRequestAddressModal
        userId = { userId }
        toggle= { setIsAddressModalOpen }
        isModalOpen= { isAddressModalOpen }
        selectedAddress={ selectedAddress }
        addresses={ addresses }
        handleSelectAddress={ handleSelectAddress }
      />
    </ConsignmentInboundRequestAddressSection>
  )
}

PickupForm.propTypes = PickupPropTypes

ConsignmentInboudRequestCreateForm.propTypes = propTypes

export default ConsignmentInboudRequestCreateForm
